import { Box, Paper, Table, TableHead, TableRow, Typography, styled } from "@mui/material";
import Grid from "@mui/material/Grid2";

export const StyledWrapper = styled(Box)(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
  padding: theme.spacing(1),
  boxSizing: "border-box",
}));

export const BackButtonWrapper = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  width: "fit-content",
  color: theme.palette.custom.packIconColor,
}));

export const BackText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.regular.fontSize,
  fontWeight: theme.typography.fontWeight.medium,
  color: theme.palette.custom.packIconColor,
  cursor: "pointer",
  transition: "color 0.2s ease",
  "&:hover": {
    color: theme.palette.custom.primary,
  },
}));

export const PageHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));

export const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.xLarge.fontSize,
  fontWeight: theme.typography.fontWeight.bold,
  color: theme.palette.custom.OrderTextColor,
}));

export const DetailCard = styled(Paper)(({ theme }) => ({
  width: "100%",
  background: theme.palette.custom.white,
  borderRadius: theme.borderRadius.radius.regular,
  padding: theme.spacing(3),
  boxSizing: "border-box",
  boxShadow: "0 6px 24px rgba(0, 0, 0, 0.045)",
  border: `1px solid ${theme.palette.custom.searchBg}`,
  transition: "box-shadow 0.2s ease, transform 0.2s ease",

  "&:hover": {
    boxShadow: "0 10px 32px rgba(0, 0, 0, 0.06)",
  },

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingBottom: theme.spacing(1.5),
  marginBottom: theme.spacing(2.5),
  borderBottom: `1px solid ${theme.palette.custom.searchBg}`,
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.large.fontSize,
  fontWeight: theme.typography.fontWeight.bold,
  color: theme.palette.custom.orderName || theme.palette.custom.OrderTextColor,
  letterSpacing: "0.1px",
}));

export const DetailRowContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(120px, 1fr) minmax(150px, 1.5fr)",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 0),
  borderBottom: `1px solid ${theme.palette.custom.searchBg}`,

  "&:last-child": {
    borderBottom: "none",
  },

  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(0.5),
    alignItems: "flex-start",
  },
}));

export const StatusBadge = styled(Box)(
  ({ theme, statusColor, statusBg }) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.75),

    minHeight: "30px",
    padding: "5px 12px",

    backgroundColor:
      statusBg || theme.palette.custom.chipsBgColor,
    color:
      statusColor || theme.palette.custom.primary,

    border: `1px solid ${
      statusColor || theme.palette.custom.primary
    }40`,

    borderRadius: "20px",

    fontSize: "11px",
    fontWeight: theme.typography.fontWeight.bold,
    lineHeight: 1,

    whiteSpace: "nowrap",
    textTransform: "none",
  })
);

export const BadgeDot = styled("span")(({ color }) => ({
  width: "7px",
  height: "7px",
  minWidth: "7px",
  borderRadius: "50%",
  backgroundColor: color || "currentColor",
}));

export const NoteBox = styled(Box)(({ theme }) => ({
  backgroundColor:
    theme.palette.custom.searchBg || "#F8F9FA",

  borderRadius: theme.borderRadius.radius.custom || "10px",

  padding: theme.spacing(1.75, 2),

  borderLeft: `3px solid ${theme.palette.custom.primary}`,

  marginTop: theme.spacing(0.75),

  "& p": {
    lineHeight: 1.6,
  },
}));

/* =========================
   Image Gallery
========================= */

export const ImageGalleryWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  width: "100%",
}));

export const PreviewImageFrame = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "360px",

  borderRadius: theme.borderRadius.radius.custom || "14px",

  overflow: "hidden",

  background:
    theme.palette.custom.searchBg || "#F7F9FB",

  border: `1px solid ${theme.palette.custom.searchBg}`,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  position: "relative",

  [theme.breakpoints.down("sm")]: {
    height: "280px",
  },
}));

export const PreviewImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
});

export const ThumbnailRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),

  width: "100%",
  overflowX: "auto",

  padding: theme.spacing(0.5),

  "&::-webkit-scrollbar": {
    height: "4px",
  },

  "&::-webkit-scrollbar-thumb": {
    borderRadius: "10px",
    backgroundColor: theme.palette.custom.primary,
  },
}));

export const ThumbnailFrame = styled(Box)(
  ({ theme, selected }) => ({
    flex: "0 0 auto",

    width: "72px",
    height: "72px",

    borderRadius: theme.borderRadius.radius.custom || "10px",

    overflow: "hidden",

    cursor: "pointer",

    backgroundColor: theme.palette.custom.white,

    border: `2px solid ${
      selected
        ? theme.palette.custom.primary
        : theme.palette.custom.searchBg
    }`,

    transition:
      "border-color 0.2s ease, transform 0.2s ease",

    transform: selected ? "scale(1.03)" : "scale(1)",

    "&:hover": {
      borderColor: theme.palette.custom.primary,
      transform: "scale(1.03)",
    },
  })
);

export const ThumbnailImg = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

/* =========================
   Invoice
========================= */

export const InvoiceDocWrapper = styled(Paper)(
  ({ theme }) => ({
    width: "100%",
    minHeight: "170px",

    boxSizing: "border-box",

    backgroundColor:
      theme.palette.custom.searchBg || "#F8FAFC",

    padding: theme.spacing(2.5),

    borderRadius:
      theme.borderRadius.radius.custom || "12px",

    border: `1px dashed ${theme.palette.custom.primary}`,

    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",

    textAlign: "center",

    gap: theme.spacing(1.25),

    boxShadow: "none",
  })
);

/* =========================
   Existing reusable styles
========================= */

export const InfoGrid = styled(Grid)(() => ({
  width: "100%",
}));

export const InfoItemLabel = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.small.fontSize,
  fontWeight: theme.typography.fontWeight.medium,
  color:
    theme.palette.custom.productTextColor ||
    theme.palette.text.secondary,
  marginBottom: "4px",
}));

export const InfoItemValue = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.regular.fontSize,
  fontWeight: theme.typography.fontWeight.semiBold,
  color:
    theme.palette.custom.OrderTextColor ||
    theme.palette.text.primary,
}));

/* =========================
   Legacy table styles
========================= */

export const ListContainer = styled(Paper)(({ theme }) => ({
  background: theme.palette.custom.white,
  padding: theme.padding.size.large,
  boxShadow: "0 8px 28px rgba(0, 0, 0, 0.04)",
  borderRadius: theme.borderRadius.radius.regular,
  border: `1px solid ${theme.palette.custom.searchBg}`,
  overflow: "hidden",
}));

export const StyledTable = styled(Table)(() => ({
  width: "100%",
  minWidth: "600px",
  borderCollapse: "separate",
  borderSpacing: "0 8px",
}));

export const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background:
    theme.palette.custom.searchBg || "#F3F3F3",

  "& th": {
    color:
      theme.palette.custom.productTextColor ||
      theme.palette.text.secondary,

    fontWeight:
      theme.typography.fontWeight.semiBold,

    border: "none",
    padding: "14px 16px",
  },
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  background: theme.palette.custom.white,

  transition:
    "transform 0.2s ease, box-shadow 0.2s ease",

  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow:
      "0 5px 16px rgba(11, 101, 181, 0.07)",
  },

  "& td": {
    padding: "14px 16px",
    borderBottom:
      `1px solid ${theme.palette.custom.searchBg}`,
  },
}));